import { Box, Tooltip, Typography } from "@mui/material";
import { DASHBOARD_COLORS as C, STATUS_COLORS, statusDot } from "../DashboardCard";
import { DEFAULTER_REASON_SEPARATOR, daysUntil, validityStatus } from "./reportConfig";

// Read by screen readers, invisible on screen.
const visuallyHidden = { position: "absolute", width: 1, height: 1, p: 0, m: "-1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 };

const VALIDITY = {
  expired: { color: STATUS_COLORS.rejected.color, label: "Expired" },
  expiring: { color: STATUS_COLORS.pending.color, label: "Expiring soon" },
};

const blank = (value) => value === undefined || value === null || String(value).trim() === "";

// Long values truncate with an ellipsis; the tooltip and title keep the full text reachable.
function Truncated({ value, sx }) {
  const text = String(value);
  const body = (
    <Typography noWrap title={text.length > 24 ? text : undefined} sx={{ minWidth: 0, ...sx }}>
      {text}
    </Typography>
  );
  return text.length > 24 ? (
    <Tooltip title={text} disableInteractive enterDelay={400}>
      {body}
    </Tooltip>
  ) : (
    body
  );
}

export function ReportCell({ column, value }) {
  if (blank(value)) return <Typography sx={{ fontSize: 13, color: "#94A3B8" }}>—</Typography>;

  if (column.type === "count") {
    const n = Number(value);
    const status = STATUS_COLORS[column.status];
    if (!status) return <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>;
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          minWidth: 32,
          justifyContent: "center",
          px: 1,
          borderRadius: "6px",
          fontSize: 13,
          lineHeight: "22px",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          color: n > 0 ? status.color : C.muted,
          bgcolor: n > 0 ? `${status.color}14` : "#F1F5F9",
        }}
      >
        {value}
      </Box>
    );
  }

  if (column.type === "date") {
    const status = VALIDITY[validityStatus(value)];
    const days = daysUntil(value);
    const hint = status ? (days < 0 ? `Expired ${-days} day${days === -1 ? "" : "s"} ago` : `Expires in ${days} day${days === 1 ? "" : "s"}`) : undefined;
    return (
      <Box component="span" title={hint} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        {status && <Box component="span" sx={statusDot(status.color)} />}
        <Typography component="span" sx={{ fontSize: 13, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: status ? status.color : "#334155", fontWeight: status ? 600 : 400 }}>
          {value}
        </Typography>
        {status && <Box component="span" sx={visuallyHidden}>{` (${status.label})`}</Box>}
      </Box>
    );
  }

  // Defaulter reasons: one compact red chip per expired document; the tooltip lists them all.
  if (column.type === "reasons") {
    const reasons = String(value).split(DEFAULTER_REASON_SEPARATOR);
    const red = STATUS_COLORS.rejected.color;
    return (
      <Tooltip title={reasons.join(", ")} disableInteractive enterDelay={400}>
        <Box component="span" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5, minWidth: 0 }}>
          {reasons.map((reason) => (
            <Box
              key={reason}
              component="span"
              sx={{ px: 0.75, borderRadius: "6px", fontSize: 11.5, lineHeight: "20px", fontWeight: 600, whiteSpace: "nowrap", color: red, bgcolor: `${red}14`, border: `1px solid ${red}33` }}
            >
              {reason}
            </Box>
          ))}
        </Box>
      </Tooltip>
    );
  }

  if (column.type === "id") return <Truncated value={value} sx={{ fontSize: 13, fontWeight: column.primary ? 600 : 500, color: column.primary ? C.navy : "#334155", fontVariantNumeric: "tabular-nums", letterSpacing: ".01em" }} />;

  return <Truncated value={value} sx={{ fontSize: column.primary ? 14 : 13.5, fontWeight: column.primary ? 600 : 400, color: column.primary ? C.navy : "#334155" }} />;
}
